import {
  type DocumentData,
  limit,
  orderBy,
  type QueryConstraint,
  startAfter,
  type Timestamp,
  where,
} from "firebase/firestore";
import { firestoreApi } from "../../core/apis/firestoreApi";
import BaseApi from "../../core/BaseApi";
import {
  BusinessFeedItem,
  type BusinessFeedItemData,
} from "./BusinessFeedItem";

export default class BusinessFeedItemsApi extends BaseApi<
  BusinessFeedItem,
  BusinessFeedItemData
> {
  /* PRIVATE APIS */

  async getFeedForBusinessPlan(
    businessPlanUid: string,
    startFromTs?: Timestamp,
  ) {
    const qs: QueryConstraint[] = [
      where("businessPlanUid", "==", businessPlanUid),
      orderBy("ts", "desc"),
      limit(5),
    ];

    if (startFromTs) {
      qs.push(startAfter(startFromTs));
    }

    const querySnapshots = await firestoreApi.queryDocuments(this.db, ...qs);

    const models: BusinessFeedItem[] = [];
    querySnapshots.forEach((querySnapshot) => {
      models.push(this.fromFirestore(querySnapshot));
    });
    this.store.load(models);
  }

  async getMediaFeedForBusinessPlan(
    businessPlanUid: string,
    startFromTs?: Timestamp,
  ) {
    const qs: QueryConstraint[] = [
      where("businessPlanUid", "==", businessPlanUid),
      where("itemData.mediaUrl", "!=", null),
      orderBy("ts", "desc"),
      limit(5),
    ];

    if (startFromTs) {
      qs.push(startAfter(startFromTs));
    }

    const querySnapshots = await firestoreApi.queryDocuments(this.db, ...qs);

    const models: BusinessFeedItem[] = [];
    querySnapshots.forEach((querySnapshot) => {
      models.push(this.fromFirestore(querySnapshot));
    });
    this.store.load(models);
  }

  async getMentionsInBusinessPlan(
    businessPlanUid: string,
    userUid?: string,
    startFromTs?: Timestamp,
  ) {
    if (!userUid) {
      throw Error("No user in mentions");
    }

    const qs: QueryConstraint[] = [
      where("businessPlanUid", "==", businessPlanUid),
      where("mentionUids", "array-contains", userUid),
      orderBy("ts", "desc"),
      limit(5),
    ];

    if (startFromTs) {
      qs.push(startAfter(startFromTs));
    }

    const querySnapshots = await firestoreApi.queryDocuments(this.db, ...qs);

    const models: BusinessFeedItem[] = [];
    querySnapshots.forEach((querySnapshot) => {
      models.push(this.fromFirestore(querySnapshot));
    });
    this.store.load(models);

    return models;
  }

  /* NEEDED BASE API OVERRIDES */

  get db() {
    return `${this.dbPrefix}business_feed_items`;
  }

  get store() {
    return this.stores.businessFeedItemsStore;
  }

  fromFirestore(res: DocumentData): BusinessFeedItem {
    return new BusinessFeedItem(this.stores, this.api, {
      uid: res.id,
      ...res.data(),
    });
  }
}
